import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Textarea, Input, Select } from "../components/UI";
import { Sparkles, Loader2, ArrowRight, LayoutTemplate } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PRESETS = [
  { label: "Candidate — Fall Media Push", prompt: "Create a modern fundraising landing page for a state senate candidate. Focus on a fall media push. Include hero, story, donation form with $10/25/50/100/250/500 amounts, recurring option, three priorities: property tax relief, safer neighborhoods, better schools. Add a Paid for by disclaimer placeholder." },
  { label: "Ballot Measure — Yes Vote", prompt: "Create a Yes on Prop 12 fundraising landing page. Bold hero explaining property tax relief for homeowners. Donation form, progress bar showing goal, three priorities blocks. Coalition-style tone. Include disclaimer." },
  { label: "PAC — Independent Expenditure", prompt: "Create a PAC fundraising page for independent expenditure supporting economic policy. Clean modern hero, donation form with higher default amounts ($250/500/1000/2500), story about the mission, disclaimer that this is not authorized by any candidate." },
];

export default function AILandingPageBuilder() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(PRESETS[0].prompt);
  const [goal, setGoal] = useState(250000);
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/political-entities").then(r => {
      setEntities(r.data);
      if (r.data[0]) setEntityId(r.data[0].id);
    });
  }, []);

  const generate = async () => {
    setLoading(true); setError(""); setPage(null);
    try {
      const { data } = await api.post("/fundraising/landing-page", {
        prompt, political_entity_id: entityId || null,
        goal_amount: parseFloat(goal) || null,
      });
      setPage(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Powered by Claude Sonnet 4.5"
        title="AI Landing Page Builder"
        subtitle="Generate a compliant fundraising landing page from a natural-language prompt. Human approval required before publishing."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Brief</div>
          <div className="space-y-3">
            <Select label="Political entity" value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              <option value="">None</option>
              {entities.map(e => <option key={e.id} value={e.id}>{e.public_campaign_name || e.legal_org_name}</option>)}
            </Select>
            <Input label="Fundraising goal (USD)" type="number" value={goal} onChange={(e) => setGoal(e.target.value)} />
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1.5">Quick presets</div>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map(p => (
                  <button key={p.label} onClick={() => setPrompt(p.prompt)}
                    className="px-2.5 py-1 rounded-full text-[11px] bg-white/5 border border-white/10 text-slate-300 hover:bg-cyan-400/10 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors">
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <Textarea label="Prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="ai-landing-prompt" />
            <Btn onClick={generate} disabled={loading || !prompt.trim()} className="w-full justify-center" data-testid="ai-landing-generate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Composing…" : "Generate landing page"}
            </Btn>
            {error && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-3">{error}</div>}
          </div>
        </Card>

        <Card className="min-h-[500px]">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Preview</div>
          {!page && !loading && (
            <div className="h-full flex items-center justify-center text-center py-10">
              <div>
                <LayoutTemplate className="w-10 h-10 text-slate-700 mx-auto" />
                <div className="mt-3 text-sm text-slate-500">Your generated page will render here</div>
              </div>
            </div>
          )}
          {loading && (
            <div className="h-full flex items-center justify-center text-center py-10">
              <div>
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
                <div className="mt-3 text-sm text-slate-400">Composing your landing page…</div>
              </div>
            </div>
          )}
          {page && <LandingPagePreview page={page} />}
        </Card>
      </div>

      {page && (
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={generate}><Sparkles className="w-4 h-4" /> Regenerate</Btn>
          <Btn onClick={() => navigate("/fundraising/landing-pages")}>Open in Landing Pages <ArrowRight className="w-4 h-4" /></Btn>
        </div>
      )}
    </div>
  );
}

function LandingPagePreview({ page }) {
  const theme = page.theme || { primary: "#fbbf24", secondary: "#d97706" };
  return (
    <div className="rounded-md border border-white/10 overflow-hidden" data-testid="landing-page-preview">
      {page.blocks.map((b, i) => (
        <div key={i} className="p-4 border-b border-white/5 last:border-b-0">
          <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold mb-1">{b.type.replace(/_/g, " ")}</div>
          {b.type === "hero" && (
            <div className="rounded-md p-4" style={{ background: `linear-gradient(135deg, ${theme.primary}22, ${theme.secondary}11)` }}>
              {b.content.eyebrow && <div className="text-xs uppercase tracking-widest" style={{ color: theme.primary }}>{b.content.eyebrow}</div>}
              <div className="font-display text-2xl font-bold mt-1">{b.content.headline}</div>
              {b.content.subheadline && <div className="text-sm text-slate-300 mt-1">{b.content.subheadline}</div>}
            </div>
          )}
          {b.type === "story" && (
            <div>
              <div className="font-semibold text-white">{b.content.title}</div>
              <p className="text-sm text-slate-300 mt-1">{b.content.body}</p>
            </div>
          )}
          {b.type === "donation_form" && (
            <div className="rounded-md border border-white/10 p-3">
              <div className="text-xs text-slate-400 mb-2">Suggested amounts</div>
              <div className="flex flex-wrap gap-2">
                {(b.content.suggested || []).map((amt, j) => (
                  <span key={j} className="px-3 py-1 rounded-full text-xs font-mono" style={{ background: theme.primary + "22", color: theme.primary }}>${amt}</span>
                ))}
              </div>
              <button className="mt-3 w-full py-2 rounded-full text-sm font-bold text-slate-900" style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }}>
                {b.content.cta || "Contribute"}
              </button>
              {b.content.recurring_enabled && <div className="text-[10px] text-slate-500 mt-2 text-center">Recurring contributions available</div>}
            </div>
          )}
          {b.type === "progress_bar" && (
            <div>
              <div className="text-xs text-slate-400 mb-1">{b.content.label}</div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full w-1/3" style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">Goal: ${b.content.goal?.toLocaleString?.() || "TBD"}</div>
            </div>
          )}
          {b.type === "priorities" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {(b.content.items || []).map((p, j) => (
                <div key={j} className="rounded-md border border-white/10 p-2">
                  <div className="font-semibold text-sm">{p.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{p.body}</div>
                </div>
              ))}
            </div>
          )}
          {b.type === "endorsements" && (
            <ul className="text-sm text-slate-300 space-y-0.5">
              {(b.content.items || []).map((e, j) => <li key={j}>· {e}</li>)}
            </ul>
          )}
          {b.type === "social_share" && (
            <div className="text-sm text-slate-300">{b.content.headline}</div>
          )}
          {b.type === "disclaimer" && (
            <div className="text-[10px] italic text-slate-500 pt-2 border-t border-white/5">{b.content.text}</div>
          )}
        </div>
      ))}
    </div>
  );
}
